import socialApiClient from "./social-api-client";

/**
 * `true`: no llama al API; devuelve {@link ADMIN_AI_MOCK_ROWS} para maquetar el dashboard.
 * Cambia a `false` cuando exista el endpoint real en producción.
 */
export const ADMIN_AI_USAGE_USE_MOCK = true;

const MOCK_DELAY_MS = 450;

/** Filas ficticias (misma forma que la respuesta real), ordenadas después por totalImages. */
const ADMIN_AI_MOCK_ROWS: AdminAiImageUsageRow[] = [
  {
    userId: "3",
    email: "ramiro@wembii.com",
    displayName: "Ramiro Anastasi",
    totalImages: 30,
  },
  {
    userId: "3503",
    email: "bastian@wembii.com",
    displayName: "Bastian Sanhueza",
    totalImages: 22,
  },
  {
    userId: "3610",
    email: "tinwehren@gmail.com",
    displayName: "Martin Wheren",
    totalImages: 4,
  },
  {
    userId: "369",
    email: "gonzalo@adlab.cl",
    displayName: "Gonzalo Bravo",
    totalImages: 3,
  },
  {
    userId: "3657",
    email: "ignacio.saez@desaezp",
    displayName: "Ignacio Saez",
    totalImages: 1,
  },
  {
    userId: "4102",
    email: "demo.creator@wembii.com",
    displayName: "Demo Creator",
    totalImages: 18,
  },
  {
    userId: "88",
    email: "beta.user@example.com",
    displayName: "Beta User",
    totalImages: 12,
  },
  {
    userId: "1200",
    email: "design.studio@example.com",
    displayName: "Design Studio",
    totalImages: 9,
  },
];

/**
 * Fila de uso de imágenes IA por usuario (admin).
 * Backend (social API): `GET /api/nanobanana/admin/usage-by-user`
 * Respuesta esperada: `{ payload: [...] }` con objetos camelCase o snake_case.
 */
export interface AdminAiImageUsageRow {
  userId: string;
  email: string;
  /** Nombre para mostrar (full name o email) */
  displayName: string;
  totalImages: number;
}

const ENDPOINT = "nanobanana/admin/usage-by-user";

function normalizeRow(raw: Record<string, unknown>): AdminAiImageUsageRow | null {
  const userId = String(raw.userId ?? raw.user_id ?? "").trim();
  const email = String(raw.email ?? "").trim();
  const totalRaw = raw.totalImages ?? raw.total_images ?? raw.image_count ?? 0;
  const total = Number(totalRaw);
  if (!Number.isFinite(total) || total < 0) return null;

  let displayName = "";
  const fn = raw.fullName ?? raw.full_name;
  if (typeof fn === "string" && fn.trim()) {
    displayName = fn.trim();
  } else {
    const first = String(raw.firstName ?? raw.first_name ?? "").trim();
    const last = String(raw.lastName ?? raw.last_name ?? "").trim();
    if (first || last) displayName = `${first} ${last}`.trim();
  }
  if (!displayName) displayName = email || userId;
  if (!userId && !email) return null;

  return {
    userId: userId || email,
    email: email || "—",
    displayName: displayName || "—",
    totalImages: total,
  };
}

export async function fetchAdminAiImageUsage(): Promise<AdminAiImageUsageRow[]> {
  if (ADMIN_AI_USAGE_USE_MOCK) {
    await new Promise((r) => setTimeout(r, MOCK_DELAY_MS));
    return [...ADMIN_AI_MOCK_ROWS].sort((a, b) => b.totalImages - a.totalImages);
  }

  const { data } = await socialApiClient().get(ENDPOINT);
  const raw = data?.payload ?? data?.data ?? data?.rows;
  const list = Array.isArray(raw) ? raw : [];
  const out: AdminAiImageUsageRow[] = [];
  for (const item of list) {
    if (item && typeof item === "object") {
      const row = normalizeRow(item as Record<string, unknown>);
      if (row) out.push(row);
    }
  }
  out.sort((a, b) => b.totalImages - a.totalImages);
  return out;
}
